import { Box, Divider, Typography } from '@mui/material';
import { Formik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { type ReactElement, useContext, useMemo } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import { type ActivityFormikType, ActivitySchema, FormikActivity } from '@core/formik/activity';
import { Activity, NewActivity } from '@core/types/api/Activity/Activity';
import type { Company } from '@core/types/api/Company/Company';
import type { Contact } from '@core/types/api/Contact/Contact';
import Enums from '@core/types/api/Enums';
import { FormatDateTimeLocal } from '@core/utils/dateHelpers';
import { hasValue } from '@core/utils/equality';
import { FormErrorNotification, SetFormikValue } from '@core/utils/formikHelpers';
import { NIL_UUID } from '@core/utils/uuid';
import { useSlabFormQuery } from '@web/hooks/useSlabFormQuery';

import { useDrawerArchive } from '../../hooks/useDrawerArchive';
import type { SharedDrawerOverrideProps } from '../../hooks/useDrawerManager';
import { useSlabMutation } from '../../hooks/useSlabMutation';
import { useSlabQuery } from '../../hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '../../utils/ApiClient';
import { DefaultEnsureDefined, lookups } from '../../utils/DomainHelpers';
import { WarningBanner } from '../Banner/WarningBanner';
import { DrawerFormFooter } from '../DrawerFormFooter/DrawerFormFooter';
import { Input } from '../Input/Input';
import { InputDropdown, type InputOption } from '../InputDropdown/InputDropdown';
import { InputMultiLookup, ReferenceValueMatcher } from '../InputMultiLookup/InputMultiLookup';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '../LookupInput/ApiLookupInput';
import { ErrorOption, LoadingOption } from '../LookupInput/ApiLookupInputBase';
import type { LookupInputOption } from '../LookupInput/LookupInputSharedComponents';
import { DrawerCloseHandler } from '../SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '../SlabDrawer/SlabDrawer';
import { SlabDrawerHeader } from '../SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '../SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '../SlabDrawer/useDrawerCloseGuard';
import { companiesForContacts, contactsForCompanies } from './ActivityTagHelpers';

type ContactQueryState = {
  isLoading: boolean;
  isError: boolean;
  items: Contact[];
};

const categoryOpts: InputOption[] = Object.values(Enums.ActivityCategory).map((s: string) => ({
  label: s,
  value: s,
}));
const statusOpts: InputOption[] = Object.values(Enums.ActivityStatus).map((s: string) => ({
  label: s,
  value: s,
}));

type ActivityDrawerProps = Omit<SharedDrawerOverrideProps<Activity>, 'resourceID' | 'onSave'> & {
  activityID: string | null;
  currentDate: DateTime;
  onSuccess: (activity: Activity) => void;
};

type ActivityDrawerContentProps = Pick<ActivityDrawerProps, 'setIsOpen' | 'onSuccess'> & {
  isCreating: boolean;
  isUpdating: boolean;
  activity: Activity | undefined;
};

const ActivityDrawerContent = ({
  isCreating,
  isUpdating,
  setIsOpen,
  onSuccess,
  activity,
}: ActivityDrawerContentProps): ReactElement => {
  const { userInfo } = useContext(SlabContext);
  const formikBag = useFormikContext<ActivityFormikType>();
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const isNew = formikBag.values.id === NIL_UUID;
  const formikCompanyIDs = useMemo(
    () => formikBag.values.companies.map((c) => c.id ?? '').filter((c) => c !== ''),
    [formikBag.values.companies],
  );

  const projectPathParams = useMemo(
    () => ({ pathParams: { id: formikBag.values.project?.id ?? '' } }),
    [formikBag.values.project?.id],
  );

  const hasProject = formikBag.values.project !== null && formikBag.values.project.id !== null;

  const {
    isLoading: isLoadingProjectCompanies,
    isError: isErrorProjectCompanies,
    data: projectCompanies,
  } = useSlabQuery('GET project companies by project ID', projectPathParams, {
    enabled: hasProject,
  });

  const {
    isLoading: isLoadingAllCompanies,
    isError: isErrorAllCompanies,
    data: allCompaniesData,
  } = useSlabQuery(
    'GET companies',
    {},
    {
      enabled: !hasProject,
    },
  );

  const projectCompanyIDs = useMemo(
    () => (projectCompanies ?? []).map((pc) => pc.company.id),
    [projectCompanies],
  );

  const companyContactQueryParams = useMemo(
    () => ({ queryParams: { id: formikCompanyIDs } }),
    [formikCompanyIDs],
  );

  const {
    isLoading: isLoadingCompanyContactList,
    isError: isErrorCompanyContactList,
    data: companyContactList,
  } = useSlabQuery('GET contacts by company IDs', companyContactQueryParams, {
    enabled: formikCompanyIDs.length > 0,
  });

  const projectContactQueryParams = useMemo(
    () => ({ queryParams: { id: projectCompanyIDs } }),
    [projectCompanyIDs],
  );

  const {
    isLoading: isLoadingProjectContacts,
    isError: isErrorProjectContacts,
    data: projectContactsData,
  } = useSlabQuery('GET contacts by company IDs', projectContactQueryParams, {
    enabled: hasProject && formikCompanyIDs.length === 0 && projectCompanyIDs.length > 0,
  });

  const {
    isLoading: isLoadingAllContacts,
    isError: isErrorAllContacts,
    data: allContactsData,
  } = useSlabQuery(
    'GET contacts',
    {},
    {
      enabled: !hasProject && formikCompanyIDs.length === 0,
    },
  );

  const {
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    data: userList,
  } = useSlabQuery('GET users', {});

  const { confirmAndArchive, isArchiveLoading, resolveIsArchived } = useDrawerArchive({
    entity: 'POST archive activity by ID',
    getIsArchived: (data) => data.isArchived,
    onSuccess: (data) => {
      setIsOpen(false);
      onSuccess(data);
    },
  });

  const loading = isLoadingUsers;

  if (loading) {
    return <LoadingSpinner />;
  }

  const error = isErrorUsers;

  const dataUndefined = userList === undefined;

  if (error || dataUndefined) {
    return <div>ERROR</div>;
  }

  const isArchived = resolveIsArchived(activity?.isArchived ?? false);

  const isRestrictedFromActivity =
    !isNew &&
    userInfo.tenant.restrictActivityEditingToAssignees &&
    !userInfo.hasPermissions([Enums.Permission.CanEditAllActivities]) &&
    activity?.creator.id !== userInfo.user.id &&
    (activity?.assignees ?? []).length > 0 &&
    !(activity?.assignees ?? []).some((assignee) => assignee.id === userInfo.user.id);

  // Archived entries stay listed only while already selected, so editing an old activity keeps them.
  const isSelectableCompany = (c: Company): boolean =>
    !c.isArchived || formikCompanyIDs.includes(c.id);
  const isSelectableContact = (c: Contact): boolean =>
    (!c.isArchived && !c.company.isArchived) ||
    formikBag.values.contacts.some((selected) => selected.id === c.id);

  const companyLookups = ((): InputOption[] => {
    if (!hasProject) {
      if (isLoadingAllCompanies) {
        return [LoadingOption];
      }
      if (isErrorAllCompanies) {
        return [ErrorOption];
      }
      const items = (allCompaniesData?.items ?? []).filter(isSelectableCompany);
      return lookups({ items, label: (c) => c.name, count: items.length });
    }

    if (isLoadingProjectCompanies) {
      return [LoadingOption];
    }
    if (isErrorProjectCompanies) {
      return [ErrorOption];
    }

    const projectCompanyItems = (projectCompanies ?? [])
      .map((pc) => pc.company)
      .filter(isSelectableCompany);
    const additionalCompanies = formikBag.values.companies
      .filter((c) => hasValue(c.id) && !projectCompanyItems.some((pc) => pc.id === c.id))
      .map((c) => ({
        id: c.id ?? '',
        name: hasValue(c.option?.label) ? c.option.label : 'Unknown Company',
        alternateID: null,
        address: {} as any,
        category: 'Contractor' as any,
        dispatchCustomer: null,
        externalID: null,
        integration: null,
        isArchived: false,
        market: null,
        notes: null,
        user: null,
      }));

    const resultCompanies = [...projectCompanyItems, ...additionalCompanies];

    return lookups({
      items: resultCompanies,
      label: (c) => c.name,
      count: resultCompanies.length,
    });
  })();

  const contactState = ((): ContactQueryState => {
    if (formikCompanyIDs.length > 0) {
      return {
        isLoading: isLoadingCompanyContactList,
        isError: isErrorCompanyContactList,
        items: (companyContactList?.items ?? []).filter((cc) =>
          formikCompanyIDs.includes(cc.company.id),
        ),
      };
    }
    if (hasProject) {
      return {
        isLoading: isLoadingProjectCompanies || isLoadingProjectContacts,
        isError: isErrorProjectCompanies || isErrorProjectContacts,
        items: projectContactsData?.items ?? [],
      };
    }
    return {
      isLoading: isLoadingAllContacts,
      isError: isErrorAllContacts,
      items: allContactsData?.items ?? [],
    };
  })();

  const contactLookups = ((): InputOption[] => {
    if (contactState.isLoading) return [LoadingOption];
    if (contactState.isError) return [ErrorOption];
    const items = contactState.items.filter(isSelectableContact);
    return lookups({
      items,
      label: (c) => c.fullName(),
      count: items.length,
    });
  })();

  const assignableUsers = userList.items.filter(
    (u) => u.isSelectable() || formikBag.values.assignees.some((a) => a.id === u.id),
  );
  const userLookups = lookups({
    items: assignableUsers,
    count: assignableUsers.length,
    label: (c) => c.fullName(),
    sublabels: (c) => c.roles.map((r) => r.displayName()),
  });

  const closeDrawer = (): void => {
    formikBag.resetForm();
    setIsOpen(false);
  };

  const requestClose = (): void =>
    guardClose({
      isDirty: !isArchived && editTracker.isEdited(),
      canSave: formikBag.isValid,
      isSaving: isCreating || isUpdating,
      onSave: formikBag.submitForm,
      onDiscard: closeDrawer,
    });

  return (
    <SlabDrawerLayout>
      <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
      <Box>
        <SlabDrawerHeader
          onClose={requestClose}
          title={
            <Typography fontSize='2rem' variant='h1'>
              {isNew ? 'Add activity' : 'Edit activity'}
            </Typography>
          }
        />

        {isRestrictedFromActivity && (
          <Box sx={{ mt: 2 }}>
            <WarningBanner title="You can't edit this activity">
              Only its assignees and creator can save changes to it.
            </WarningBanner>
          </Box>
        )}

        <Box paddingTop='1.25rem' display='flex' flexDirection='column'>
          <Input name='name' label='Name*' />
          <Input name='description' label='Description' type='textarea' />

          <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
            <Input
              sx={{ flex: 1, flexBasis: 160 }}
              name='startTime'
              label='Start Time*'
              type='datetime'
              onInputChange={async (val): Promise<void> => {
                const start = DateTime.fromISO(val);
                const end = DateTime.fromISO(formikBag.values.endTime);

                // If start is later than end, set end to start + 30 mins.
                if (start >= end) {
                  SetFormikValue(
                    formikBag,
                    'endTime',
                    FormatDateTimeLocal(start.plus({ minutes: 30 })),
                  );
                }
              }}
            />
            <Input
              sx={{ flex: 1, flexBasis: 160 }}
              name='endTime'
              label='End Time*'
              type='datetime'
              onInputChange={async (val): Promise<void> => {
                const start = DateTime.fromISO(formikBag.values.startTime);
                const end = DateTime.fromISO(val);

                // If start is later than end, set start to end - 30 mins.
                if (start >= end) {
                  SetFormikValue(
                    formikBag,
                    'startTime',
                    FormatDateTimeLocal(end.minus({ minutes: 30 })),
                  );
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', columnGap: 2, flexWrap: 'wrap' }}>
            <InputDropdown
              sx={{ flex: 1, flexBasis: 160 }}
              label='Category'
              name='category'
              options={categoryOpts}
            />
            <InputDropdown
              sx={{ flex: 1, flexBasis: 160 }}
              label='Status'
              name='status'
              options={statusOpts}
            />
          </Box>

          <InputMultiLookup
            name='assignees'
            label='Assignees'
            options={userLookups}
            onMatchChange={(matches): void => {
              SetFormikValue(
                formikBag,
                'assignees',
                matches.map(({ value, label }) => ({
                  id: value,
                  option: {
                    value,
                    label,
                  },
                })),
              );
            }}
            formValueMatcher={ReferenceValueMatcher}
          />
        </Box>

        <Divider />

        <Box paddingY='.75rem' display='flex' flexDirection='column'>
          <ApiLookupInput
            formState={formikBag.values}
            name='project'
            label='Project'
            route={{
              barrel: 'GET project summaries',
              args: (
                inputText,
                { constructQueryParams },
              ): QueryRouteBarrelTypes['GET project summaries']['args'] =>
                constructQueryParams(inputText, {
                  filterBy: [
                    {
                      name: 'isArchived',
                      operation: Enums.FilterOperation.Equals,
                      values: [false],
                    },
                  ],
                }),
              transform: (pl): LookupInputOption[] =>
                lookups({
                  ...pl,
                  label: (p) => p.name,
                }),
            }}
            onMatchChange={(): void => {
              SetFormikValue(formikBag, 'companies', []);
              SetFormikValue(formikBag, 'contacts', []);
            }}
          />
          <InputMultiLookup
            name='companies'
            label='Companies'
            options={companyLookups}
            onMatchChange={(matches): void => {
              SetFormikValue(
                formikBag,
                'companies',
                matches.map(({ value, label }) => ({
                  id: value,
                  option: {
                    value,
                    label,
                  },
                })),
              );
              const contacts = contactsForCompanies(
                matches.map((m) => m.value),
                formikBag.values.contacts,
                companyContactList?.items ?? [],
              );
              SetFormikValue(formikBag, 'contacts', contacts);
            }}
            formValueMatcher={ReferenceValueMatcher}
          />
          <InputMultiLookup
            name='contacts'
            label='Contacts'
            options={contactLookups}
            onMatchChange={(matches): void => {
              SetFormikValue(
                formikBag,
                'contacts',
                matches.map(({ value, label }) => ({
                  id: value,
                  option: { value, label },
                })),
              );
              let allKnownContacts = allContactsData?.items ?? [];
              if (formikCompanyIDs.length > 0) {
                allKnownContacts = companyContactList?.items ?? [];
              } else if (hasProject) {
                allKnownContacts = projectContactsData?.items ?? [];
              }
              const existingCompanyIDs = new Set(formikBag.values.companies.map((c) => c.id ?? ''));
              const newCompanies = companiesForContacts(matches, allKnownContacts).filter(
                (c) => !existingCompanyIDs.has(c.id),
              );
              if (newCompanies.length > 0) {
                SetFormikValue(formikBag, 'companies', [
                  ...formikBag.values.companies,
                  ...newCompanies.map((company) => ({
                    id: company.id,
                    option: { value: company.id, label: company.name },
                  })),
                ]);
              }
            }}
            formValueMatcher={ReferenceValueMatcher}
          />
        </Box>
      </Box>
      <DrawerFormFooter
        isArchived={isArchived}
        isNew={isNew}
        hasEntity={hasValue(activity)}
        isArchiveLoading={isArchiveLoading}
        isSaving={isCreating || isUpdating}
        isSaveDisabled={!formikBag.dirty || isCreating || isUpdating}
        isResetDisabled={!formikBag.dirty || isCreating || isUpdating || isArchiveLoading}
        onArchive={(): void => {
          if (hasValue(activity)) confirmAndArchive({ id: activity.id, isArchived });
        }}
        onCancel={requestClose}
        onReset={(): void => formikBag.resetForm()}
        onSave={formikBag.submitForm}
      />
    </SlabDrawerLayout>
  );
};

export const ActivityDrawerPage = ({
  activityID,
  currentDate,
  setIsOpen,
  onSuccess,
  onError,
  ensureDefined = DefaultEnsureDefined,
}: ActivityDrawerProps): ReactElement | null => {
  const isNew = activityID === null;

  const createNew = useSlabMutation('POST activity', { onSuccess, onError });
  const updateExisting = useSlabMutation('PUT activity by ID', { onSuccess, onError });

  const {
    isLoading: isLoadingActivity,
    isError: isErrorActivity,
    data: maybeActivity,
  } = useSlabFormQuery(
    'GET activity by ID',
    {
      pathParams: {
        id: activityID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const {
    isLoading: isLoadingActivityCompanies,
    isError: isErrorActivityCompanies,
    data: activityCompanies,
  } = useSlabFormQuery(
    'GET companies by activity ID',
    {
      pathParams: {
        id: activityID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const {
    isLoading: isLoadingActivityContacts,
    isError: isErrorActivityContacts,
    data: activityContacts,
  } = useSlabFormQuery(
    'GET contacts by activity ID',
    {
      pathParams: {
        id: activityID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const fullActivityUndefined =
    maybeActivity === undefined ||
    activityContacts === undefined ||
    activityCompanies === undefined;

  const activity = ensureDefined(
    fullActivityUndefined
      ? undefined
      : NewActivity({
          ...maybeActivity,
          companies: activityCompanies,
          contacts: activityContacts,
        }),
  );

  const loading = isLoadingActivity || isLoadingActivityCompanies || isLoadingActivityContacts;

  const error = isErrorActivity || isErrorActivityCompanies || isErrorActivityContacts;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>ERROR</div>;
  }

  const formikActivity = FormikActivity({ currentDate }, activity);

  return (
    <Formik
      validationSchema={ActivitySchema}
      initialValues={formikActivity}
      onSubmit={(values): void => {
        // TODO #2219 Need a NewActivityFromForm wrapper for this case
        if (formikActivity.id === NIL_UUID) {
          createNew.mutate({ args: { body: new Activity(values) }, schema: ActivitySchema });
        } else {
          updateExisting.mutate({
            args: {
              pathParams: { id: formikActivity.id },
              body: new Activity(values),
            },
            schema: ActivitySchema,
          });
        }
      }}
    >
      <>
        <FormErrorNotification />
        <ActivityDrawerContent
          activity={maybeActivity}
          isCreating={createNew.isPending}
          isUpdating={updateExisting.isPending}
          setIsOpen={setIsOpen}
          onSuccess={onSuccess}
        />
      </>
    </Formik>
  );
};

export const ActivityDrawer = (props: ActivityDrawerProps): ReactElement => (
  <SlabDrawer isOpen={props.isOpen} close={() => props.setIsOpen(false)}>
    <ActivityDrawerPage {...props} />
  </SlabDrawer>
);
